import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Search, BedDouble, Eye, RefreshCw, User, Mail, Phone, MapPin, Calendar, Users, MessageSquare, Star, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import PageLoader from "../components/PageLoader";
import EmptyState from "../components/EmptyState";
import StatusBadge from "../components/StatusBadge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


const STATUS_OPTIONS = ["all", "enquiry", "contacted", "quoted", "converted", "cancelled"];

function DetailField({ icon: Icon, label, children }) {
  if (!children && children !== 0) return null;
  return (
    <div className="flex gap-3 py-2.5 border-b border-gray-50 last:border-0">
      {Icon && <Icon className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />}
      <div className="min-w-0">
        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">{label}</p>
        <div className="text-sm text-gray-800">{children}</div>
      </div>
    </div>
  );
}

function calcNights(checkIn, checkOut) {
  try {
    const diff = new Date(checkOut) - new Date(checkIn);
    const n = Math.round(diff / 86400000);
    return n > 0 ? n : 0;
  } catch { return 0; }
}

export default function HotelRequests() {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [converting, setConverting] = useState(false);
  const [agentNotes, setAgentNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const params = statusFilter !== "all" ? { status: statusFilter } : {};
      const response = await axios.get(`${API}/hotel-requests`, { headers: getAuthHeaders(), params });
      setRequests(response.data);
    } catch {
      toast.error("Failed to fetch hotel requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRequests(); }, [statusFilter]);

  const openDetail = (req) => {
    setSelectedRequest(req);
    setAgentNotes(req.agent_notes || "");
  };

  const updateStatus = async (newStatus) => {
    if (!selectedRequest) return;
    setUpdatingStatus(true);
    try {
      await axios.patch(`${API}/hotel-requests/${selectedRequest.id}`, { status: newStatus }, { headers: getAuthHeaders() });
      const updated = { ...selectedRequest, status: newStatus };
      setSelectedRequest(updated);
      setRequests(prev => prev.map(r => r.id === updated.id ? updated : r));
      toast.success(`Status updated to ${newStatus}`);
    } catch {
      toast.error("Failed to update status");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const saveNotes = async () => {
    if (!selectedRequest) return;
    setSavingNotes(true);
    try {
      await axios.patch(`${API}/hotel-requests/${selectedRequest.id}`, { agent_notes: agentNotes }, { headers: getAuthHeaders() });
      const updated = { ...selectedRequest, agent_notes: agentNotes };
      setSelectedRequest(updated);
      setRequests(prev => prev.map(r => r.id === updated.id ? updated : r));
      toast.success("Notes saved");
    } catch {
      toast.error("Failed to save notes");
    } finally {
      setSavingNotes(false);
    }
  };

  const convertToBooking = async () => {
    if (!selectedRequest) return;
    setConverting(true);
    try {
      const r = await axios.post(`${API}/hotel-requests/${selectedRequest.id}/convert`, {}, { headers: getAuthHeaders() });
      toast.success("Hotel booking created!");
      setSelectedRequest(null);
      navigate(`/hotel-bookings/${r.data.hotel_booking_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to convert to hotel booking");
    } finally {
      setConverting(false);
    }
  };

  const filtered = requests.filter(r => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return (
      r.first_name?.toLowerCase().includes(q) ||
      r.last_name?.toLowerCase().includes(q) ||
      r.email?.toLowerCase().includes(q) ||
      r.destination?.toLowerCase().includes(q) ||
      r.request_number?.toLowerCase().includes(q)
    );
  });

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 py-6">
        <PageHeader
          title="Hotel Requests"
          subtitle={`${requests.length} request${requests.length !== 1 ? "s" : ""}`}
          actions={
            <Button variant="outline" size="sm" onClick={fetchRequests} disabled={loading}>
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              Refresh
            </Button>
          }
        />

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-5">
          <div className="relative flex-1 min-w-48">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search name, email, destination, ref..."
              className="pl-9"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map(s => (
                <SelectItem key={s} value={s}>{s === "all" ? "All Statuses" : s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        {loading ? (
          <PageLoader inline message="Loading hotel requests…" />
        ) : filtered.length === 0 ? (
          <EmptyState
            title="No hotel requests"
            description={searchTerm || statusFilter !== "all" ? "Try adjusting your filters." : "Hotel requests will appear here once submitted."}
            icon={BedDouble}
          />
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Ref</TableHead>
                    <TableHead className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Customer</TableHead>
                    <TableHead className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Destination</TableHead>
                    <TableHead className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Check-in</TableHead>
                    <TableHead className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Check-out</TableHead>
                    <TableHead className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Rooms</TableHead>
                    <TableHead className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Guests</TableHead>
                    <TableHead className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</TableHead>
                    <TableHead className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(req => (
                    <TableRow
                      key={req.id}
                      className="hover:bg-indigo-50/30 cursor-pointer"
                      onClick={() => openDetail(req)}
                    >
                      <TableCell className="font-mono text-xs text-indigo-700 font-semibold">{req.request_number || "—"}</TableCell>
                      <TableCell>
                        <div className="font-medium text-gray-900">{req.first_name} {req.last_name}</div>
                        <div className="text-xs text-gray-400">{req.email}</div>
                      </TableCell>
                      <TableCell className="text-gray-700">
                        <div>{req.destination}</div>
                        {req.country && <div className="text-xs text-gray-400">{req.country}</div>}
                      </TableCell>
                      <TableCell className="text-gray-600 text-sm">{req.check_in || "—"}</TableCell>
                      <TableCell className="text-gray-600 text-sm">{req.check_out || "—"}</TableCell>
                      <TableCell className="text-gray-600 text-sm">{req.num_rooms || 1}</TableCell>
                      <TableCell className="text-gray-600 text-sm">{(req.adults || 1) + (req.children || 0)}</TableCell>
                      <TableCell>
                        <StatusBadge status={req.status} />
                      </TableCell>
                      <TableCell className="text-gray-500 text-xs">{req.created_at?.slice(0, 10)}</TableCell>
                      <TableCell>
                        <Eye size={15} className="text-indigo-500" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {/* Detail Dialog */}
        {selectedRequest && (
          <Dialog open={!!selectedRequest} onOpenChange={open => { if (!open) setSelectedRequest(null); }}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <BedDouble size={18} className="text-indigo-600" />
                  Hotel Request — {selectedRequest.request_number}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-5 mt-2">
                {/* Status + Convert */}
                <div className="flex flex-wrap items-center gap-3">
                  <StatusBadge status={selectedRequest.status} />
                  <Select value={selectedRequest.status} onValueChange={updateStatus} disabled={updatingStatus || selectedRequest.status === "converted"}>
                    <SelectTrigger className="w-36 h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {["enquiry", "contacted", "quoted", "cancelled"].map(s => (
                        <SelectItem key={s} value={s} className="text-xs capitalize">{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedRequest.status !== "converted" ? (
                    <Button
                      size="sm"
                      onClick={convertToBooking}
                      disabled={converting}
                      className="bg-indigo-700 hover:bg-indigo-800 text-white ml-auto"
                    >
                      <ArrowRight size={14} />
                      {converting ? "Converting..." : "Convert to Hotel Booking"}
                    </Button>
                  ) : (
                    selectedRequest.hotel_booking_id && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setSelectedRequest(null); navigate(`/hotel-bookings/${selectedRequest.hotel_booking_id}`); }}
                        className="ml-auto"
                      >
                        View Hotel Booking →
                      </Button>
                    )
                  )}
                </div>

                {/* Customer */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Customer</p>
                  <DetailField icon={User} label="Name">{selectedRequest.first_name} {selectedRequest.last_name}</DetailField>
                  <DetailField icon={Mail} label="Email">{selectedRequest.email}</DetailField>
                  <DetailField icon={Phone} label="Phone">{selectedRequest.phone}</DetailField>
                </div>

                {/* Trip */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Trip Details</p>
                  <DetailField icon={MapPin} label="Destination">{selectedRequest.destination}{selectedRequest.country ? `, ${selectedRequest.country}` : ""}</DetailField>
                  <DetailField icon={Calendar} label="Dates">
                    {selectedRequest.check_in} → {selectedRequest.check_out}
                    {selectedRequest.check_in && selectedRequest.check_out && (
                      <span className="ml-2 text-gray-500">({calcNights(selectedRequest.check_in, selectedRequest.check_out)} nights)</span>
                    )}
                  </DetailField>
                  <DetailField icon={Users} label="Guests">
                    {selectedRequest.num_rooms} room{selectedRequest.num_rooms !== 1 ? "s" : ""} · {selectedRequest.adults} adult{selectedRequest.adults !== 1 ? "s" : ""}
                    {selectedRequest.children > 0 && ` · ${selectedRequest.children} child${selectedRequest.children !== 1 ? "ren" : ""}`}
                    {selectedRequest.child_ages?.length > 0 && ` (ages: ${selectedRequest.child_ages.join(", ")})`}
                  </DetailField>
                </div>

                {/* Preferences */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Preferences</p>
                  {selectedRequest.has_specific_hotel && selectedRequest.hotel_name_preference && (
                    <DetailField icon={BedDouble} label="Specific Hotel">{selectedRequest.hotel_name_preference}</DetailField>
                  )}
                  <DetailField icon={Star} label="Star Rating">
                    {selectedRequest.star_rating === "no_preference" ? "No preference" : `${selectedRequest.star_rating} star`}
                  </DetailField>
                  <DetailField label="Room Type">{selectedRequest.room_type === "no_preference" ? "No preference" : selectedRequest.room_type?.replace(/_/g, " ")}</DetailField>
                  <DetailField label="Meal Plan">{selectedRequest.meal_plan === "no_preference" ? "No preference" : selectedRequest.meal_plan?.replace(/_/g, " ")}</DetailField>
                  {selectedRequest.budget_per_night && (
                    <DetailField label="Budget / Night">€{selectedRequest.budget_per_night}</DetailField>
                  )}
                </div>

                {/* Special Requests */}
                {selectedRequest.special_requests && (
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Special Requests</p>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedRequest.special_requests}</p>
                  </div>
                )}

                {/* Agent Notes */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Agent Notes (Internal)</p>
                  <textarea
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none bg-white"
                    rows={3}
                    placeholder="Add internal notes..."
                    value={agentNotes}
                    onChange={e => setAgentNotes(e.target.value)}
                  />
                  <Button size="sm" variant="outline" onClick={saveNotes} disabled={savingNotes} className="mt-2">
                    <MessageSquare size={13} />
                    {savingNotes ? "Saving..." : "Save Notes"}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </DashboardLayout>
  );
}
